import { useState } from 'react';
import {
  useMealFeedback,
  useSaveMealTemplate,
  useTrainerClientNutrition,
  useTrainerClientNutritionDays,
} from '../../api/queries';
import { QueryBoundary } from '../../components/QueryBoundary';
import { useTranslation } from '../../i18n/I18nProvider';
import { TemplateForm } from './TrainerMealTemplates';
import { toErrorMessage } from '../../i18n/errorMessage';
import { addDays, formatDate, formatNumber, todayIso } from '../../lib/format';
import { MEAL_SLOT_KEY } from '../../lib/nutrition';
import type { MealOutput } from '../../api/types';
import {
  Button,
  Card,
  MealCard,
  Modal,
  Select,
  SkeletonList,
  StatTile,
  Textarea,
  useToast,
} from '../../ui';

/**
 * Prehrana klijenta na trenerovom ekranu.
 *
 * Klijent upisuje obroke i slika ih zato da ih netko pogleda; do sada taj
 * dnevnik nije imao čitatelja. Ekran namjerno nije uređivački — trener ne
 * mijenja tuđe unose, nego ostavlja palac ili komentar.
 *
 * Padajući popis nudi samo dane koji stvarno imaju upis; strelice ostaju za
 * susjedne dane. Bez popisa bi trener klikao unatrag kroz prazne datume ne
 * znajući gdje ima ičega.
 */
export function ClientNutrition({ clientId }: { clientId: string }) {
  const { t, locale } = useTranslation();
  const [day, setDay] = useState(todayIso());
  /* Uvećana fotografija; `null` znači zatvoreno. Drži je sekcija, ne kartica —
     dijalog mora nadživjeti prekrivanje kartice pri osvježavanju popisa. */
  const [slika, setSlika] = useState<{ url: string; naziv: string } | null>(null);
  const query = useTrainerClientNutrition(clientId, day);
  const daysQuery = useTrainerClientNutritionDays(clientId);

  const dani = daysQuery.data?.days ?? [];

  return (
    <section data-testid="client-nutrition">
      <h2 className="eyebrow mb-1">{t('trainer.clientNutrition')}</h2>
      <p className="mb-3 text-[13px] text-ink-muted">{t('trainer.clientNutritionHelp')}</p>

      <div className="mb-3 flex flex-wrap items-end gap-2">
        <Button variant="secondary" size="sm" onClick={() => setDay((d) => addDays(d, -1))}>
          ← {t('nutrition.prevDay')}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={day >= todayIso()}
          onClick={() => setDay((d) => addDays(d, 1))}
        >
          {t('nutrition.nextDay')} →
        </Button>
        {dani.length > 0 && (
          <Select
            id="nutrition-day"
            label={t('trainer.nutritionDayPick')}
            value={dani.some((d) => d.day === day) ? day : ''}
            onChange={(e) => setDay(e.target.value || day)}
            options={[
              { value: '', label: formatDate(`${day}T00:00:00.000Z`, locale) },
              ...dani.map((d) => ({
                value: d.day,
                label: `${formatDate(`${d.day}T00:00:00.000Z`, locale)} · ${d.count}`,
              })),
            ]}
          />
        )}
      </div>

      <QueryBoundary
        query={query}
        skeleton={<SkeletonList rows={2} />}
        isEmpty={(d) => d.meals.length === 0}
        empty={{ message: t('trainer.clientNutritionEmpty') }}
      >
        {(data) => (
          <div className="flex flex-col gap-4">
            <Card>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <StatTile
                  label={t('nutrition.kcal')}
                  value={formatNumber(data.totals.kcal, locale)}
                  unit="kcal"
                />
                <StatTile
                  label={t('nutrition.protein')}
                  value={formatNumber(data.totals.proteinG, locale)}
                  unit="g"
                />
                <StatTile
                  label={t('nutrition.carbs')}
                  value={formatNumber(data.totals.carbsG, locale)}
                  unit="g"
                />
                <StatTile
                  label={t('nutrition.water')}
                  value={formatNumber(data.waterMl / 1000, locale, 1)}
                  unit="l"
                />
              </div>
            </Card>

            <ul className="flex flex-col gap-3">
              {data.meals.map((meal) => (
                <li key={meal.id}>
                  <ObrokSPovratnom
                    clientId={clientId}
                    day={day}
                    meal={meal}
                    onOpenPhoto={setSlika}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}
      </QueryBoundary>

      {slika && (
        <Modal
          open
          onClose={() => setSlika(null)}
          title={slika.naziv}
          closeLabel={t('common.close')}
        >
          <img
            src={slika.url}
            alt={t('nutrition.mealPhoto')}
            className="mx-auto max-h-[70vh] w-auto max-w-full rounded-md"
          />
        </Modal>
      )}
    </section>
  );
}

function ObrokSPovratnom({
  clientId,
  day,
  meal,
  onOpenPhoto,
}: {
  clientId: string;
  day: string;
  meal: MealOutput;
  onOpenPhoto: (slika: { url: string; naziv: string }) => void;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const feedback = useMealFeedback(clientId, day);
  const spremiPredlozak = useSaveMealTemplate();
  const [otvoren, setOtvoren] = useState(false);
  const [predlozakOtvoren, setPredlozakOtvoren] = useState(false);
  const [tekst, setTekst] = useState(meal.trainerComment ?? '');

  const naziv = t(MEAL_SLOT_KEY[meal.slot]);
  const svidjaSe = meal.trainerLikedAt !== null;
  const foto = meal.photoUrls[0] ?? null;

  async function posalji(promjena: { comment?: string | null; liked?: boolean }) {
    try {
      await feedback.mutateAsync({ mealId: meal.id, ...promjena });
      if (promjena.comment !== undefined) setOtvoren(false);
    } catch (err) {
      toast(toErrorMessage(t, err).title, 'danger');
    }
  }

  /*
    Palac i komentar su dva odgovora na istu stvar, pa stoje jedan uz drugi.
    Prije je palac visio gore desno uz vrijeme, a „Komentiraj" dolje lijevo —
    dvije radnje istog para na dva kraja kartice.
  */
  const palac = (
    <Button
      variant={svidjaSe ? 'primary' : 'secondary'}
      size="sm"
      /* Ime obroka je u nazivu jer je na ekranu pet jednakih gumba. */
      aria-label={t(svidjaSe ? 'trainer.mealUnlikeOf' : 'trainer.mealLikeOf', { meal: naziv })}
      aria-pressed={svidjaSe}
      disabled={feedback.isPending}
      onClick={() => void posalji({ liked: !svidjaSe })}
    >
      {svidjaSe ? `✓ ${t('trainer.mealLike')}` : t('trainer.mealLike')}
    </Button>
  );

  const komentarUnos = otvoren ? (
    <div className="flex flex-col gap-2 border-t border-line-subtle pt-3">
      <Textarea
        id={`komentar-${meal.id}`}
        label={t('trainer.mealCommentLabel', { meal: naziv })}
        rows={3}
        maxLength={1000}
        value={tekst}
        onChange={(e) => setTekst(e.target.value)}
      />
      <div className="flex flex-wrap gap-2">
        <Button size="sm" loading={feedback.isPending} onClick={() => void posalji({ comment: tekst })}>
          {t('common.save')}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setTekst(meal.trainerComment ?? '');
            setOtvoren(false);
          }}
        >
          {t('common.cancel')}
        </Button>
      </div>
    </div>
  ) : (
    <div className="flex flex-wrap items-center gap-2">
      {palac}
      <Button variant="secondary" size="sm" onClick={() => setOtvoren(true)}>
        {meal.trainerComment ? t('trainer.mealCommentEdit') : t('trainer.mealComment')}
      </Button>
      {/*
        Jelovnik koji treba prvo utipkati ostaje prazan. Obroke svojih klijenata
        trener ionako pregledava svaki dan — kad naiđe na dobar, ovdje ga spremi
        umjesto da ga poslije piše iz glave.
      */}
      {meal.description && (
        <Button variant="ghost" size="sm" onClick={() => setPredlozakOtvoren(true)}>
          {t('mealTemplates.fromMeal')}
        </Button>
      )}
    </div>
  );

  const predlozakObrazac = predlozakOtvoren ? (
    <TemplateForm
      naslov={t('mealTemplates.fromMealTitle')}
      predlozak={{
        // Opis obroka zna biti dug, a naziv predloška je kratak — trener ga
        // ionako dorađuje prije spremanja.
        name: (meal.description ?? '').trim().slice(0, 120),
        slot: meal.slot,
        kcal: meal.kcal,
        proteinG: meal.proteinG,
        carbsG: meal.carbsG,
        fatG: meal.fatG,
        note: null,
      }}
      pending={spremiPredlozak.isPending}
      onClose={() => setPredlozakOtvoren(false)}
      onSubmit={(input) =>
        spremiPredlozak.mutate(
          { input },
          {
            onSuccess: () => {
              setPredlozakOtvoren(false);
              toast(t('mealTemplates.saved'), 'success');
            },
            onError: (error) => toast(toErrorMessage(t, error).title, 'danger'),
          },
        )
      }
    />
  ) : null;

  return (
    <MealCard
      name={naziv}
      /* Dan piše u biraču iznad; ponovljen na svakoj kartici je samo šum. */
      timeLabel=""
      note={meal.description}
      photoUrl={foto}
      trainerComment={meal.trainerComment}
      trainerCommentLabel={t('nutrition.trainerComment')}
      macros={{
        kcal: meal.kcal ?? 0,
        proteinG: meal.proteinG ?? 0,
        carbsG: meal.carbsG ?? 0,
        fatG: meal.fatG ?? 0,
      }}
      labels={{
        kcal: 'kcal',
        protein: t('nutrition.protein'),
        carbs: t('nutrition.carbs'),
        fat: t('nutrition.fat'),
      }}
      onPhotoOpen={foto ? () => onOpenPhoto({ url: foto, naziv }) : undefined}
      photoOpenLabel={t('nutrition.photoOpen')}
      footer={
        <>
          {komentarUnos}
          {predlozakObrazac}
        </>
      }
    />
  );
}
